<template>
  <div>
    <!-- Loading Screen -->
    <div
      v-if="isLoading"
      class="fixed inset-0 flex items-center justify-center bg-[#fafbfc] z-[99999]"
    >
      <LottieLoader :src="lottieJson" />
    </div>

    <!-- Main Content -->
    <div
      v-else
      class="min-h-screen bg-gradient-to-br from-[#e8ecf1] to-[#d8dfe7] flex flex-col"
    >
      <!-- Header -->
      <header
        class="fixed top-0 left-0 right-0 bg-gradient-to-r from-[#5A6670] to-[#3A444E] text-white shadow-lg z-50 rounded-b-2xl"
      >
        <div class="w-full px-4 py-3 flex items-center justify-center relative">
          <button
            class="absolute left-4 p-2 hover:bg-white/20 rounded-lg transition-colors duration-150"
            style="z-index: 2"
            @click="goBack"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div class="flex flex-col items-center justify-center">
            <h1 class="font-bold text-lg leading-tight text-center">
              Keranjang
            </h1>
            <span
              class="font-bold text-xs px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white mt-1 text-center border border-white/30"
              style="letter-spacing: 0.5px"
            >
              {{ getOrderTypeDisplay() }}
            </span>
          </div>
        </div>
      </header>

      <!-- Padding atas agar isi tidak ketutup header -->
      <div style="height: 90px" />

      <!-- Main Content -->
      <main
        ref="mainContent"
        class="flex-1 pt-2 pb-32 overflow-y-auto px-4"
      >
        <div class="w-full space-y-3">
          <!-- Cart Items -->
          <div
            v-for="(item, index) in cart"
            :key="
              item.id +
                '-' +
                (item.varian || '') +
                (item.isLocked ? '-locked' : '')
            "
            class="bg-white rounded-2xl p-4 shadow-sm border border-[#e8ecf1]"
          >
            <!-- Header dengan Nama Menu, Badge, dan Quantity Control -->
            <div class="flex flex-col gap-3 mb-3">
              <!-- Nama dan Badge -->
              <div class="flex items-center gap-2">
                <h3 class="font-bold text-[#2c3e50] text-base">
                  {{ item.name }}
                </h3>
                <!-- Tampilkan badge varian jika ada -->
                <span
                  v-if="item.varian"
                  :class="[
                    'inline-block px-2 py-1 rounded-lg font-bold text-xs border',
                    getVariantStyle(item),
                  ]"
                  style="min-width: 45px; text-align: center"
                >
                  {{ getVariantDisplay(item) }}
                </span>
              </div>

              <!-- Quantity Control - Di pojok kanan, bawah nama menu -->
              <div class="flex items-center space-x-2 justify-end">
                <button
                  class="w-7 h-7 bg-[#5A6670] hover:bg-[#3A444E] text-white rounded-lg flex items-center justify-center transition-colors duration-150 text-base font-bold"
                  @click="decreaseQuantity(index)"
                >
                  −
                </button>
                <span
                  class="w-8 text-center font-semibold text-[#2c3e50] text-base"
                >{{ item.quantity }}</span>
                <button
                  class="w-7 h-7 bg-[#5A6670] hover:bg-[#3A444E] text-white rounded-lg flex items-center justify-center transition-colors duration-150 text-base font-bold"
                  @click="increaseQuantity(index)"
                >
                  +
                </button>
              </div>
            </div>

            <!-- Additional Section - Display Only (Selected from MenuPage) -->
            <div
              v-if="hasSelectedAdditional(item)"
              class="mb-3"
            >
              <div
                class="bg-[#f0f8ff] rounded-xl p-3 border border-[#cce7ff] shadow-sm"
              >
                <div class="flex items-center gap-2">
                  <svg
                    class="w-4 h-4 text-[#3A5A7A] flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="text-xs font-semibold text-[#3A5A7A]">{{
                    getSelectedAdditionalName(item)
                  }}</span>
                  <span class="text-xs font-bold text-[#3A5A7A] ml-auto">+Rp
                    {{ formatPrice(getSelectedAdditionalPrice(item)) }}</span>
                </div>
              </div>
            </div>

            <!-- Note -->
            <div class="mt-3">
              <label
                class="text-xs text-[#7f8c8d] font-medium mb-2 block text-left"
              >Catatan pesanan</label>
              <textarea
                :ref="`textarea-${index}`"
                v-model="item.note"
                :maxlength="100"
                placeholder="Catatan untuk pesanan ini (opsional)"
                class="w-full p-2.5 border border-[#e8ecf1] rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-[#5A6670] focus:border-[#5A6670] transition-colors duration-150 bg-white"
                rows="2"
                @input="handleNoteInput(index, $event)"
                @keydown="handleKeyDown($event)"
              />
              <div class="flex justify-end mt-1">
                <span
                  class="text-xs font-medium"
                  :class="getCounterColor(item.note.length)"
                >
                  {{ item.note.length }}/100
                </span>
              </div>
            </div>

            <!-- Price Breakdown - Tersusun di Paling Bawah -->
            <div
              class="text-xs text-[#2c3e50] mt-3 bg-[#f8f9fa] rounded-xl p-3 border border-[#e8ecf1]"
            >
              <div class="flex justify-between">
                <span class="text-[#7f8c8d]">Harga satuan</span>
                <span class="text-[#2c3e50] font-semibold">Rp {{ formatPrice(item.basePrice || item.price) }}</span>
              </div>

              <!-- Tampilkan harga additional jika ada -->
              <div
                v-if="hasSelectedAdditional(item)"
                class="flex justify-between text-[#7f8c8d] mt-1.5 pt-1.5 border-t border-[#e8ecf1]"
              >
                <span>{{ getSelectedAdditionalName(item) }}</span>
                <span>+Rp {{ formatPrice(getSelectedAdditionalPrice(item)) }}</span>
              </div>

              <div
                class="flex justify-between font-semibold mt-2 pt-2 border-t border-[#e8ecf1] text-[#2c3e50]"
              >
                <span>Subtotal</span>
                <span>Rp {{ formatPrice(calculateItemSubtotal(item)) }}</span>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-if="cart.length === 0"
            class="text-center py-16"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-[#e8ecf1] rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-[#7f8c8d]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
            </div>
            <p class="text-[#2c3e50] font-semibold mb-1">
              Keranjang kosong
            </p>
            <p class="text-sm text-[#7f8c8d]">
              Silakan pilih menu terlebih dahulu
            </p>
          </div>
        </div>

        <!-- Spacer agar card tidak nempel ke footer -->
        <div
          v-if="cart.length > 0"
          style="height: 4px"
        />
      </main>

      <!-- Footer -->
      <footer
        v-if="cart.length > 0"
        class="fixed bottom-0 left-0 right-0 bg-white shadow-2xl z-40 rounded-t-2xl border-t border-[#e8ecf1]"
      >
        <div
          class="w-full px-4 py-4 flex flex-col items-center justify-center gap-2"
        >
          <!-- Info Items & Harga horizontal -->
          <div class="flex flex-row items-center justify-center w-full gap-4">
            <span class="font-medium text-[#2c3e50] text-xs">
              Total Items:
              <span class="text-[#5A6670] font-bold text-base">{{
                cart.length
              }}</span>
            </span>
            <span class="font-medium text-[#2c3e50] text-xs">
              Total Harga:
              <span
                class="font-bold text-base text-[#2c3e50]"
                style="min-width: 90px"
              >Rp {{ formatPrice(totalPrice) }}</span>
            </span>
          </div>
          <!-- Tombol di bawah, full width -->
          <button
            class="bg-[#5A6670] hover:bg-[#3A444E] text-white px-4 py-2.5 rounded-xl font-semibold transition-colors duration-150 text-sm w-full"
            style="height: 44px"
            @click="showOrderConfirm = true"
          >
            Pesan Sekarang
          </button>
        </div>
      </footer>

      <!-- Order Confirmation Dialog -->
      <transition name="fade-dialog">
        <div
          v-if="showOrderConfirm"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <div
            class="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl animate-scale-in"
          >
            <div class="text-center mb-6">
              <div
                class="w-12 h-12 bg-[#d4edda] rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <svg
                  class="w-6 h-6 text-[#28a745]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 class="text-lg font-bold text-[#2c3e50] mb-2">
                Konfirmasi Pesanan
              </h3>
              <p class="text-[#7f8c8d] text-sm">
                Apakah Anda yakin ingin memesan semua item ini?
              </p>
            </div>
            <div class="flex space-x-3">
              <button
                class="flex-1 bg-[#5A6670] hover:bg-[#3A444E] text-white py-3 rounded-xl font-semibold transition-all duration-150 hover:scale-105"
                @click="confirmOrder"
              >
                Ya, Pesan
              </button>
              <button
                class="flex-1 bg-[#e8ecf1] hover:bg-[#d8dfe7] text-[#2c3e50] py-3 rounded-xl font-semibold transition-all duration-150 hover:scale-105"
                @click="showOrderConfirm = false"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Delete Confirmation Dialog -->
      <transition name="fade-dialog">
        <div
          v-if="showDeleteConfirm"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <div
            class="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl animate-scale-in"
          >
            <div class="text-center mb-6">
              <div
                class="w-12 h-12 bg-[#f8d7da] rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <svg
                  class="w-6 h-6 text-[#dc3545]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <h3 class="text-lg font-bold text-[#2c3e50] mb-2">
                Hapus Item
              </h3>
              <p class="text-[#7f8c8d] text-sm">
                Hapus
                <span class="font-semibold text-[#2c3e50]">{{
                  itemToDelete?.name
                }}</span>
                dari keranjang?
              </p>
            </div>
            <div class="flex space-x-3">
              <button
                class="flex-1 bg-[#5A6670] hover:bg-[#3A444E] text-white py-3 rounded-xl font-semibold transition-all duration-150 hover:scale-105"
                @click="deleteItemConfirmed"
              >
                Hapus
              </button>
              <button
                class="flex-1 bg-[#e8ecf1] hover:bg-[#d8dfe7] text-[#2c3e50] py-3 rounded-xl font-semibold transition-all duration-150 hover:scale-105"
                @click="cancelDelete"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Stock Error Dialog -->
      <transition name="fade-dialog">
        <div
          v-if="showStockError"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
        >
          <div
            class="bg-white rounded-2xl w-full max-w-md shadow-xl animate-scale-in max-h-[80vh] flex flex-col"
          >
            <!-- Scrollable Content -->
            <div class="overflow-y-auto flex-1 p-6">
              <div class="text-center">
                <div
                  class="w-16 h-16 bg-[#f8d7da] rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <svg
                    class="w-8 h-8 text-[#dc3545]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.732-1.333-2.464 0L5.268 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 class="text-xl font-bold text-[#2c3e50] mb-3">
                  Stok Tidak Cukup!
                </h3>
                <p class="text-sm text-[#7f8c8d] mb-4">
                  {{
                    stockErrorDetails.length > 1
                      ? `Ada ${stockErrorDetails.length} item yang stok tidak mencukupi:`
                      : "Item berikut stok tidak mencukupi:"
                  }}
                </p>

                <!-- List semua item yang error -->
                <div class="space-y-3 mb-4">
                  <div
                    v-for="(error, index) in stockErrorDetails"
                    :key="index"
                    class="text-left bg-[#f8d7da] rounded-lg p-4 border-l-4 border-[#dc3545]"
                  >
                    <p class="text-sm text-[#2c3e50] font-semibold mb-2">
                      {{ error.itemName }}
                    </p>
                    <div class="space-y-1 text-sm text-[#7f8c8d]">
                      <div class="flex justify-between">
                        <span>Stok tersedia:</span>
                        <span class="font-semibold text-[#dc3545]">{{
                          error.available
                        }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span>Yang dipesan:</span>
                        <span class="font-semibold">{{ error.ordered }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p class="text-sm text-[#7f8c8d]">
                  Silakan kurangi jumlah pesanan atau hapus item dari keranjang.
                </p>
              </div>
            </div>

            <!-- Fixed Button at Bottom -->
            <div
              class="p-6 pt-4 border-t border-[#e8ecf1] bg-white rounded-b-2xl"
            >
              <button
                class="w-full bg-[#5A6670] hover:bg-[#3A444E] text-white py-3 rounded-xl font-semibold transition-all duration-150 hover:scale-105"
                @click="closeStockError"
              >
                Mengerti
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { createOrder } from "@/api";
import api from "@/api";
import LottieLoader from "@/components/LottieLoader.vue";
import lottieJson from "../assets/loader.json";

export default {
  name: "CartPage",
  components: { LottieLoader },
  data() {
    return {
      cart: [],
      tableNumber: "-",
      orderType: null,
      customerName: "",
      showOrderConfirm: false,
      showDeleteConfirm: false,
      itemToDelete: null,
      itemToDeleteIndex: null,
      isLoading: true,
      lottieJson,
      showStockError: false,
      stockErrorDetails: [],
    };
  },

  computed: {
    totalPrice() {
      return this.cart.reduce(
        (sum, item) => sum + this.calculateItemSubtotal(item),
        0
      );
    },
  },

  async mounted() {
    this.isLoading = true;
    await this.loadData();
    this.isLoading = false;
  },

  methods: {
    async loadData() {
      try {
        const cartData = localStorage.getItem("cart");
        this.cart = cartData ? JSON.parse(cartData) : [];

        this.tableNumber = localStorage.getItem("tableNumber") || "-";
        this.orderType = localStorage.getItem("orderType") || null;
        this.customerName = localStorage.getItem("customerName") || "";

        // Normalize cart items
        this.cart = this.cart.map((item) => {
          // Migrate old field names
          if (item.tipe_minuman && !item.varian) {
            item.varian = item.tipe_minuman;
            delete item.tipe_minuman;
          }

          // Initialize fields
          if (!item.note) item.note = "";
          if (!item.selectedAdditionalId) item.selectedAdditionalId = null;
          if (!item.basePrice && item.price) item.basePrice = item.price;
          if (!item.additionals) item.additionals = [];

          // Set isLocked based on selected additional
          if (item.isLocked === undefined) {
            item.isLocked = !!item.selectedAdditionalId;
          }

          return item;
        });

        this.saveCart();
      } catch (error) {
        console.error("Error loading cart data:", error);
        this.cart = [];
      }
    },

    saveCart() {
      try {
        localStorage.setItem("cart", JSON.stringify(this.cart));
      } catch (error) {
        console.error("Error saving cart:", error);
      }
    },

    getOrderTypeDisplay() {
      let displayText = "";

      if (this.orderType === "TAKEAWAY" && this.customerName) {
        displayText = `TAKEAWAY | ${this.customerName}`;
      } else if (this.orderType === "DINE-IN" && this.tableNumber !== "-") {
        displayText = `DINE-IN | Meja ${this.tableNumber}`;
      } else if (this.tableNumber !== "-") {
        displayText = `DINE-IN | Meja ${this.tableNumber}`;
      } else {
        displayText = "Informasi Pesanan";
      }

      return displayText;
    },

    formatPrice(price) {
      return new Intl.NumberFormat("id-ID").format(price);
    },

    getCounterColor(length) {
      if (length >= 100) return "text-red-600";
      if (length >= 90) return "text-orange-600";
      if (length >= 80) return "text-yellow-600";
      return "text-[#7f8c8d]";
    },

    hasVariant(item) {
      // Cek apakah item punya varian yang dipilih
      return !!item.varian;
    },

    getVariantStyle(item) {
      if (!item.varian) return "bg-gray-100 text-gray-600 border-gray-300";

      const variantName = item.varian.name || item.varian;

      // Dynamic color based on variant name
      if (
        variantName.toLowerCase().includes("panas") ||
        variantName.toLowerCase().includes("hot")
      ) {
        return "bg-red-100 text-red-600 border-red-300";
      }

      if (
        variantName.toLowerCase().includes("dingin") ||
        variantName.toLowerCase().includes("ice") ||
        variantName.toLowerCase().includes("cold")
      ) {
        return "bg-blue-100 text-blue-600 border-blue-300";
      }

      return "bg-purple-100 text-purple-600 border-purple-300";
    },

    getVariantDisplay(item) {
      if (!item.varian) return "";
      return item.varian.name || item.varian;
    },

    // ===== ADDITIONAL METHODS (DISPLAY ONLY) =====
    hasSelectedAdditional(item) {
      return !!item.selectedAdditionalId;
    },

    getSelectedAdditionalName(item) {
      if (!item.selectedAdditionalId || !item.additionals) return "";
      const selected = item.additionals.find(
        (a) => a.id === item.selectedAdditionalId
      );
      return selected ? selected.name : "";
    },

    getSelectedAdditionalPrice(item) {
      if (!item.selectedAdditionalId || !item.additionals) return 0;
      const selected = item.additionals.find(
        (a) => a.id === item.selectedAdditionalId
      );
      return selected ? selected.price : 0;
    },

    calculateItemSubtotal(item) {
      const basePrice = item.basePrice || item.price;
      let subtotal = basePrice * item.quantity;

      // Add additional price if selected
      if (item.selectedAdditionalId) {
        const additionalPrice = this.getSelectedAdditionalPrice(item);
        subtotal += additionalPrice * item.quantity;
      }

      return subtotal;
    },

    handleNoteInput(index, event) {
      const textarea = event.target;

      if (textarea.scrollHeight > textarea.clientHeight) {
        this.$nextTick(() => {
          textarea.scrollTop = textarea.scrollHeight;
          this.scrollToTextarea(index);
        });
      }

      this.saveCart();
    },

    handleKeyDown(event) {
      if (event.key === "Enter" && !event.shiftKey) {
        this.$nextTick(() => {
          event.target.scrollTop = event.target.scrollHeight;
        });
      }
    },

    scrollToTextarea(index) {
      const textarea = this.$refs[`textarea-${index}`];
      if (textarea && textarea[0]) {
        const element = textarea[0];
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (rect.bottom > windowHeight - 150) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }
    },

    increaseQuantity(index) {
      this.cart[index].quantity += 1;
      this.saveCart();
    },

    decreaseQuantity(index) {
      const item = this.cart[index];
      if (item.quantity === 1) {
        this.confirmDeleteItem(index);
      } else {
        item.quantity -= 1;
        this.saveCart();
      }
    },

    confirmDeleteItem(index) {
      this.itemToDelete = this.cart[index];
      this.itemToDeleteIndex = index;
      this.showDeleteConfirm = true;
    },

    deleteItemConfirmed() {
      if (this.itemToDeleteIndex !== null) {
        this.cart.splice(this.itemToDeleteIndex, 1);
        this.saveCart();
      }
      this.cancelDelete();
    },

    cancelDelete() {
      this.showDeleteConfirm = false;
      this.itemToDelete = null;
      this.itemToDeleteIndex = null;
    },

    async confirmOrder() {
      this.showOrderConfirm = false;
      this.isLoading = true;

      try {
        await this.submitOrder();
      } catch (error) {
        console.error("Error during order confirmation:", error);
        alert("Terjadi kesalahan saat memproses pesanan. Silakan coba lagi.");
      } finally {
        this.isLoading = false;
      }
    },

    async submitOrder() {
      const orderType = localStorage.getItem("orderMode");
      const customerName = localStorage.getItem("customerName");
      const tableNumber = localStorage.getItem("tableNumber");

      // ✅ VALIDATE STOCK & AVAILABILITY
      try {
        await this.validateCartAvailability();
      } catch (error) {
        console.error("❌ Validation failed:", error);
        this.isLoading = false;
        return;
      }

      // Prepare order items
      const items = this.cart.map((item) => {
        const orderItem = {
          menu_id: parseInt(item.id),
          variant_id: item.varian ? parseInt(item.varian.id) : null,
          quantity: item.quantity,
        };

        // Add note if exists
        if (item.note && item.note.trim()) {
          orderItem.note = item.note.trim();
        }

        // Add selected additional if exists
        if (item.selectedAdditionalId) {
          orderItem.selected_additional_id = parseInt(
            item.selectedAdditionalId
          );
        }

        return orderItem;
      });

      // Prepare order data
      const orderData = {
        order_type: orderType === "dine-in" ? "dine-in" : "take-away",
        identifier: orderType === "dine-in" ? tableNumber : customerName,
        discount: 0,
        items: items,
      };

      console.log("📤 Submitting order:", orderData);

      try {
        const response = await createOrder(orderData);
        console.log("✅ Order created successfully:", response);

        // Clear cart
        this.cart = [];
        localStorage.setItem("cart", "[]");

        // Redirect to order page
        const sessionHash = localStorage.getItem("sessionHash");
        if (sessionHash) {
          await this.$router.push({
            name: "OrderPage",
            params: { hash: sessionHash },
          });
        } else {
          await this.$router.push({ name: "SelectTable" });
        }
      } catch (error) {
        console.error("❌ Failed to create order:", error);

        // Check if it's a stock error
        const errorMessage =
          error?.response?.data?.message || error?.message || "";
        if (
          errorMessage.toLowerCase().includes("stok") ||
          errorMessage.toLowerCase().includes("stock") ||
          errorMessage.toLowerCase().includes("available")
        ) {
          this.showStockError = true;
          this.stockErrorDetails = [
            {
              itemName: "Item pesanan",
              available: "Tidak tersedia",
              ordered: "Mohon periksa kembali",
            },
          ];
        }

        throw error;
      }
    },

    // ✅ NEW: Validate cart availability before submit
    async validateCartAvailability() {
      const unavailableItems = [];

      for (const item of this.cart) {
        try {
          // Fetch latest menu data
          const response = await api.get(`/menus/${item.id}`);
          const menuData = response.data.data || response.data;

          console.log(`🔍 Checking ${item.name}:`, {
            menu_available: menuData.is_available,
            variant_id: item.varian?.id,
            additional_id: item.selectedAdditionalId,
            menuData: menuData,
          });

          // Check menu availability
          if (!menuData.is_available) {
            unavailableItems.push({
              itemName: item.name,
              available: "Stok Habis",
              ordered: `${item.quantity} porsi`,
            });
            continue; // ✅ Skip ke item berikutnya
          }

          // Check variant availability (hanya jika ada variant dipilih)
          if (item.varian && item.varian.id) {
            const selectedVariant = menuData.variants?.find(
              (v) => v.id === parseInt(item.varian.id)
            );

            if (selectedVariant && !selectedVariant.is_available) {
              unavailableItems.push({
                itemName: `${item.name} - ${item.varian.name}`,
                available: "Varian Tidak Tersedia",
                ordered: `${item.quantity} porsi`,
              });
              continue; // ✅ Skip ke item berikutnya
            }
          }

          // Check additional availability (hanya jika ada additional dipilih)
          if (item.selectedAdditionalId) {
            const selectedAdditional = menuData.additionals?.find(
              (a) => a.id === parseInt(item.selectedAdditionalId)
            );

            if (selectedAdditional && !selectedAdditional.is_available) {
              const additionalName = this.getSelectedAdditionalName(item);
              unavailableItems.push({
                itemName: `${item.name} + ${additionalName}`,
                available: "Tambahan Tidak Tersedia",
                ordered: `${item.quantity} porsi`,
              });
              continue; // ✅ Skip ke item berikutnya
            }
          }

          // ✅ Item ini available, lanjut ke item berikutnya
          console.log(`✅ ${item.name} is available`);
        } catch (error) {
          console.error(`❌ Error validating ${item.name}:`, error);
          unavailableItems.push({
            itemName: item.name,
            available: "Gagal Validasi",
            ordered: `${item.quantity} porsi`,
          });
        }
      }

      console.log(
        `📊 Validation result: ${unavailableItems.length} unavailable items`
      );

      // Show error if any items unavailable
      if (unavailableItems.length > 0) {
        this.stockErrorDetails = unavailableItems;
        this.showStockError = true;
        throw new Error("Some items are unavailable");
      }

      console.log("✅ All items are available!");
    },

    closeStockError() {
      this.showStockError = false;
      this.stockErrorDetails = [];
    },

    goBack() {
      if (this.$router && window.history.length > 1) {
        this.$router.go(-1);
      } else if (this.$router) {
        this.$router.push({ name: "MenuPage" }).catch(() => {
          window.history.back();
        });
      } else {
        window.history.back();
      }
    },
  },
};
</script>

<style scoped>
.text-red-500 {
  color: #dc3545;
}

.text-red-500:hover {
  color: #c82333;
}

.hover\:text-red-700:hover {
  color: #ad1d28;
}

.transition-all {
  transition: all 0.3s ease;
}

.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: ease-in-out;
}

.fade-dialog-enter-active,
.fade-dialog-leave-active {
  transition: opacity 0.3s ease;
}

.fade-dialog-enter-from,
.fade-dialog-leave-to {
  opacity: 0;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.animate-scale-in {
  animation: scaleIn 0.3s ease-out;
}

textarea::-webkit-scrollbar {
  width: 4px;
}

textarea::-webkit-scrollbar-track {
  background: #fafbfc;
  border-radius: 2px;
}

textarea::-webkit-scrollbar-thumb {
  background: #d8dfe7;
  border-radius: 2px;
}

textarea::-webkit-scrollbar-thumb:hover {
  background: #5a6670;
}

main {
  scroll-behavior: smooth;
}

button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

button:active:not(:disabled) {
  transform: translateY(0);
}

button:disabled {
  cursor: not-allowed;
}

* {
  animation-play-state: running !important;
}

/* Counter colors */
.text-red-600 {
  color: #dc3545;
}

.text-orange-600 {
  color: #fd7e14;
}

.text-yellow-600 {
  color: #ffc107;
}
</style>
